import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, User, ArrowLeft, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BlogCard from '@/components/BlogCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    setLoading(true);
    setPost(null);
    setRelatedPosts([]);
    let active = true;
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blogs/${slug}`);
        if (!active) return;
        setPost(response.data);
        
        const relatedRes = await axios.get(`${API}/blogs?category=${response.data.category}&limit=3`);
        const filtered = relatedRes.data.filter(p => p.slug !== slug);
        if (active) setRelatedPosts(filtered.slice(0, 3));
      } catch (error) {
        console.error('Error fetching blog post:', error);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchPost();
    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-[132px] pb-12">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto h-96 bg-secondary animate-pulse rounded-sm" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-[132px] pb-12">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h1 className="text-3xl font-playfair font-bold mb-4">Post Not Found</h1>
          <Button asChild><Link to="/blog" data-testid="missing-post-back-to-deals">Back to Deals for Students</Link></Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const shareUrl = window.location.href;
  const shareTitle = post.title;

  return (
    <div className="pt-[132px] pb-12 bg-white">
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-2 text-primary hover:text-secondary hover:underline mb-6 transition-colors" data-testid="back-to-blog">
            <ArrowLeft size={16} />
            Back to Deals for Students
          </Link>

          {/* Article Header */}
          <article>
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="bg-primary text-primary-foreground px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-sm" data-testid="article-category">
                  {post.category}
                </span>
                {post.is_sponsored && (
                  <span className="bg-accent text-accent-foreground px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-sm" data-testid="article-sponsored-label">
                    Sponsored
                  </span>
                )}
              </div>
              <h1 className="text-4xl md:text-5xl font-playfair font-bold text-foreground mb-4 break-words" data-testid="article-title">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground mb-4">
                <span className="flex items-center gap-2" data-testid="article-author">
                  <User size={16} />
                  {post.author_name}
                </span>
                <span className="flex items-center gap-2" data-testid="article-date">
                  <Calendar size={16} />
                  {formatDate(post.published_date)}
                </span>
                <span className="flex items-center gap-2" data-testid="article-read-time">
                  <Clock size={16} />
                  {post.read_time} min read
                </span>
              </div>
            </div>

            {/* Featured Image */}
            <div className="mb-8">
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-auto rounded-sm"
                data-testid="article-image"
              />
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none blog-content text-foreground mb-8 break-words" data-testid="article-content" dangerouslySetInnerHTML={{ __html: post.content }} />

            {/* Tags */}
            <div className="mb-8">
              <h3 className="text-lg font-playfair font-bold mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, idx) => (
                  <span key={idx} className="bg-secondary text-secondary-foreground px-3 py-1 text-sm rounded-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Share Buttons */}
            <div className="border-t border-b py-6 mb-8">
              <h3 className="text-lg font-playfair font-bold mb-4">Share this article</h3>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  onClick={() => window.open(`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}
                  data-testid="share-facebook"
                >
                  <Facebook size={16} className="mr-2" />
                  Facebook
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`, '_blank')}
                  data-testid="share-twitter"
                >
                  <Twitter size={16} className="mr-2" />
                  Twitter
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  onClick={() => window.open(`https://linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank')}
                  data-testid="share-linkedin"
                >
                  <Linkedin size={16} className="mr-2" />
                  LinkedIn
                </Button>
              </div>
            </div>
          </article>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div>
              <h2 className="text-2xl md:text-3xl font-playfair font-bold text-foreground mb-6">Related Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <BlogCard key={relatedPost.post_id} post={relatedPost} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}